/**
 * 🔐 Utilitários de Permissões RBAC (Frontend)
 * 
 * Formato: plataforma:recurso[:sub-recurso]:ação
 * Plataformas: all, web, app, api
 * 
 * Regras:
 * - "all:X" faz match com qualquer "web:X", "app:X", "api:X"
 * - "manage" implica view, create, edit, delete
 * - domAdministrador = true → bypass total
 */

/** Ações implícitas quando se tem "manage" */
const MANAGE_IMPLIES: ReadonlySet<string> = new Set(['view', 'create', 'edit', 'delete']);

/**
 * Extrai a plataforma e o restante da permissão
 * Ex: "all:corporativo:cadastros:alunos:manage" → { platform: "all", rest: "corporativo:cadastros:alunos:manage" }
 */
function splitPermission(perm: string): { platform: string; rest: string } {
  const idx = perm.indexOf(':');
  if (idx === -1) return { platform: perm, rest: '' };
  return { platform: perm.substring(0, idx), rest: perm.substring(idx + 1) };
}

/**
 * Extrai a ação (último segmento) de uma permissão
 * Ex: "corporativo:cadastros:alunos:manage" → { path: "corporativo:cadastros:alunos", action: "manage" }
 */
function splitAction(rest: string): { path: string; action: string } {
  const idx = rest.lastIndexOf(':');
  if (idx === -1) return { path: '', action: rest };
  return { path: rest.substring(0, idx), action: rest.substring(idx + 1) };
}

/**
 * Verifica se o array de permissões contém a permissão solicitada.
 * 
 * Regras de match:
 * 1. Match direto: permissão exata encontrada
 * 2. Match via "all:": se required é "web:X", aceita "all:X" 
 * 3. Match via "manage": se required é "web:X:view", aceita "web:X:manage" ou "all:X:manage"
 */
export function hasPermission(permissoes: string[], required: string): boolean {
  if (!permissoes || permissoes.length === 0 || !required) return false;

  // 1. Match direto
  if (permissoes.includes(required)) return true;

  const { platform: reqPlatform, rest: reqRest } = splitPermission(required);
  const { path: reqPath, action: reqAction } = splitAction(reqRest);

  for (const perm of permissoes) {
    const { platform: permPlatform, rest: permRest } = splitPermission(perm);

    // Verificar se a plataforma faz match
    // Match direto de plataforma OU "all" faz match com qualquer plataforma
    const platformMatch = permPlatform === reqPlatform || permPlatform === 'all';
    if (!platformMatch) continue;

    // Se o rest é igual, já é match (ex: all:X matches web:X)
    if (permRest === reqRest) return true;

    // Verificar match via "manage" → implica view/create/edit/delete
    const { path: permPath, action: permAction } = splitAction(permRest);
    if (permPath === reqPath && permAction === 'manage' && MANAGE_IMPLIES.has(reqAction)) {
      return true;
    }
  }

  return false;
}

/**
 * Verifica se tem QUALQUER uma das permissões requeridas (OR)
 */
export function hasAnyPermission(permissoes: string[], required: string[]): boolean {
  return required.some(perm => hasPermission(permissoes, perm));
}

/**
 * Verifica se tem TODAS as permissões requeridas (AND)
 */
export function hasAllPermissions(permissoes: string[], required: string[]): boolean {
  return required.every(perm => hasPermission(permissoes, perm));
}

/**
 * Retorna os setores visíveis para o conjunto de permissões.
 * Um setor é visível se existir qualquer permissão referenciando-o.
 * 
 * Setores reconhecidos: corporativo, escolar, fretamento
 */
export function getVisibleSectors(permissoes: string[]): string[] {
  const sectors = new Set<string>();
  const knownSectors = ['corporativo', 'escolar', 'fretamento'];

  for (const perm of permissoes) {
    const { rest } = splitPermission(perm);
    const firstSegment = rest.split(':')[0];
    if (knownSectors.includes(firstSegment)) {
      sectors.add(firstSegment);
    }
  }

  return Array.from(sectors);
}

/**
 * Retorna os módulos visíveis dentro de um setor.
 * Ex: getVisibleModules(perms, 'corporativo') → ['cadastros', 'processos', 'relatorios']
 */
export function getVisibleModules(permissoes: string[], setor: string): string[] {
  const modules = new Set<string>();

  for (const perm of permissoes) {
    const { rest } = splitPermission(perm);
    const segments = rest.split(':');
    // formato: setor:modulo:...:ação
    if (segments[0] === setor && segments.length >= 3) {
      modules.add(segments[1]);
    }
  }

  return Array.from(modules);
}

/**
 * Retorna as abas visíveis dentro de um módulo.
 * Ex: getVisibleTabs(perms, 'corporativo', 'cadastros') → ['alunos', 'escolas', ...]
 */
export function getVisibleTabs(permissoes: string[], setor: string, modulo: string): string[] {
  const tabs = new Set<string>();

  for (const perm of permissoes) {
    const { rest } = splitPermission(perm);
    const segments = rest.split(':');
    // formato: setor:modulo:aba:ação
    if (segments[0] === setor && segments[1] === modulo && segments.length >= 4) {
      tabs.add(segments[2]);
    }
  }

  return Array.from(tabs);
}

/**
 * A regra do menu clássico, em função pura (spec 0087): o que `canView` do
 * `usePermissions` responde, sem o React. O menu do topo e o teste dele leem a
 * mesma regra.
 *
 * - com aba: `web:setor:modulo:aba:view` (ou `all:`/`manage`, por `hasPermission`);
 * - só com módulo: alguma aba desse módulo em qualquer permissão;
 * - só com setor: o setor aparece em alguma permissão — e só `corporativo`,
 *   `escolar` e `fretamento` são reconhecidos, então o ViVan fica para quem tem
 *   `domAdministrador`. A Roteirização não passa por aqui: o setor dela pergunta
 *   pela permissão da API (spec 0098).
 */
export function podeVer(permissoes: string[], setor: string, modulo?: string, aba?: string): boolean {
  if (modulo && aba) return hasPermission(permissoes, `web:${setor}:${modulo}:${aba}:view`);
  if (modulo) return getVisibleTabs(permissoes, setor, modulo).length > 0;
  return getVisibleSectors(permissoes).includes(setor);
}
